import { replaceVerbatim } from "@project/shared/src/replaceVerbatim";
import { ShaderChunk } from "three";

/**
 * Geometric specular anti-aliasing for every physical material in the scene.
 *
 * A specular highlight is a function of the normal; where the normal changes
 * a lot from one pixel to the next — the die lines and grooves on the
 * aluminium, the foam's pores, the edge fillets seen from a distance — one
 * pixel catches the highlight and its neighbour does not, and the surface
 * sparkles as the camera moves. Supersampling would resolve it; SMAA, being a
 * post-process on the final image, cannot see a highlight that was never
 * wide enough to leave an edge.
 *
 * Tokuyoshi & Kaplanyan ("Improved Geometric Specular Antialiasing", 2019)
 * instead widen the highlight where it would sparkle: the screen-space
 * derivatives of the shading normal measure how much normal one pixel covers,
 * that variance is folded into the roughness lobe, and the lobe stays exactly
 * as it was wherever the normal is smooth across the pixel. The constants are
 * Filament's (`specularAntiAliasingVariance` 0.15, `...Threshold` 0.2), which
 * it treats as its production defaults.
 *
 * Three does a version of this already — `geometryRoughness`, the largest
 * component of the derivatives of the *un-perturbed* normal, added to the
 * roughness. That filters the geometry but not the normal map, and the normal
 * maps are where our sparkle is. This replaces that term for the base lobe
 * with the filtered one on the perturbed normal; the clearcoat keeps three's
 * term, since it has no normal map of its own here.
 *
 * Patched into `ShaderChunk.lights_physical_fragment` like the specular
 * occlusion, so it needs no per-material hook and leaves hardware-instancing
 * eligibility alone.
 */
export const GEOMETRIC_SPECULAR_AA_VARIANCE = 0.15;
export const GEOMETRIC_SPECULAR_AA_THRESHOLD = 0.2;

const MARKER = "GEOMETRIC_SPECULAR_AA";

/** The line in three's chunk that this replaces. */
const THREE_GEOMETRY_ROUGHNESS_LINE =
  "material.roughness += geometryRoughness;";

const GLSL = /* glsl */ `
{
	// ${MARKER}: Tokuyoshi & Kaplanyan 2019, Filament's constants.
	// \`normal\` is the shading normal after the normal map; three's own
	// \`geometryRoughness\` (derivatives of the un-perturbed normal) is
	// subsumed by it and left to the clearcoat below.
	vec3 gsaaDu = dFdx( normal );
	vec3 gsaaDv = dFdy( normal );
	float gsaaVariance = ${GEOMETRIC_SPECULAR_AA_VARIANCE.toFixed(4)} * ( dot( gsaaDu, gsaaDu ) + dot( gsaaDv, gsaaDv ) );
	float gsaaKernel = min( 2.0 * gsaaVariance, ${GEOMETRIC_SPECULAR_AA_THRESHOLD.toFixed(4)} );
	float gsaaAlpha = material.roughness * material.roughness;
	material.roughness = sqrt( sqrt( clamp( gsaaAlpha * gsaaAlpha + gsaaKernel, 0.0, 1.0 ) ) );
}`;

/**
 * Patches the shared chunk. Idempotent; safe to call from any module that
 * renders physical materials. Throws if three's chunk no longer has the line
 * it replaces, so an upgrade fails loudly rather than silently losing the
 * filter.
 */
export function enableGeometricSpecularAntiAliasing(): void {
  const current = ShaderChunk.lights_physical_fragment;
  if (current.includes(MARKER)) return;
  if (!current.includes(THREE_GEOMETRY_ROUGHNESS_LINE)) {
    throw new Error(
      `Geometric specular AA: three's lights_physical_fragment has no "${THREE_GEOMETRY_ROUGHNESS_LINE}"`,
    );
  }
  ShaderChunk.lights_physical_fragment = replaceVerbatim(
    current,
    THREE_GEOMETRY_ROUGHNESS_LINE,
    GLSL,
  );
}

/**
 * JS port of the filter, for tests and for reasoning about magnitudes.
 *
 * @param roughness Perceptual roughness (three's `material.roughness`).
 * @param du Screen-space X derivative of the shading normal.
 * @param dv Screen-space Y derivative of the shading normal.
 */
export function geometricSpecularAaRoughness(
  roughness: number,
  du: readonly [number, number, number],
  dv: readonly [number, number, number],
): number {
  const dot = (a: readonly number[]) => a.reduce((s, x) => s + x * x, 0);
  const variance = GEOMETRIC_SPECULAR_AA_VARIANCE * (dot(du) + dot(dv));
  const kernel = Math.min(2 * variance, GEOMETRIC_SPECULAR_AA_THRESHOLD);
  const alpha = roughness * roughness;
  const squared = Math.min(Math.max(alpha * alpha + kernel, 0), 1);
  return Math.sqrt(Math.sqrt(squared));
}
